import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useEffect, useRef } from 'react';
import { useTheme } from '@emotion/react';
import { darkTheme, lightTheme } from '../../../components/highcharts/themes';
import { formatNumber } from '@/utils/formatters';
import { DateTime } from 'luxon';

Highcharts.setOptions({
  lang: {
    decimalPoint: ',',
    thousandsSep: '.',
    months: [
      'januar',
      'februar',
      'marts',
      'april',
      'maj',
      'juni',
      'juli',
      'august',
      'september',
      'oktober',
      'november',
      'december',
    ],
    weekdays: [
      'søndag',
      'mandag',
      'tirsdag',
      'onsdag',
      'torsdag',
      'fredag',
      'lørdag',
    ],
    shortMonths: [
      'jan',
      'feb',
      'mar',
      'apr',
      'maj',
      'jun',
      'jul',
      'aug',
      'sep',
      'okt',
      'nov',
      'dec',
    ],
  },
}); /**
 * ColumnChart
 * Props:
 * - data: { categories: string[], series: [{ name, data: number[] }] }
 * - title: string
 * - xLabel: string
 * - unit: string (optional)
 * - isLoading: boolean (optional)
 */
const ColumnChart = ({
  data = { categories: [], series: [] },
  title = '',
  xLabel = '',
  unit = '',
  isLoading = false,
  height = 400,
}) => {
  const chartRef = useRef(null);
  const theme = useTheme();
  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    const themeOptions =
      theme.palette?.mode === 'dark' ? darkTheme : lightTheme;

    chart.update(
      {
        chart: { backgroundColor: themeOptions.chart.backgroundColor },
        title: { style: { color: themeOptions.title.style.color } },
        xAxis: {
          lineColor: themeOptions.xAxis.lineColor,
          labels: { style: { color: themeOptions.xAxis.labels.style.color } },
          title: {
            style: themeOptions.xAxis.title?.style || {
              color: themeOptions.xAxis.title?.style?.color,
            },
          },
        },
        yAxis: [
          {
            labels: { style: { color: themeOptions.yAxis.labels.style.color } },
          },
        ],
        legend: { itemStyle: { color: themeOptions.legend.itemStyle.color } },
        tooltip: {
          backgroundColor: themeOptions.tooltip.backgroundColor,
          borderColor: themeOptions.tooltip.borderColor,
          style: { color: theme.palette.mode === 'dark' ? 'white' : 'black' },
        },
      },
      false,
    );
    chart.redraw();
  }, [theme]);

  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return;
    if (isLoading) chart.showLoading('Loading data...');
    else chart.hideLoading();
  }, [isLoading]);

  const options = {
    chart: { type: 'column' },
    title: { text: title || '' },
    xAxis: {
      type: 'datetime',
      crosshair: { width: 1, color: 'gray', dashStyle: 'shortdot' },

      title: { text: xLabel || '' },
    },
    yAxis: {
      min: 0,
      crosshair: { width: 1, color: 'gray', dashStyle: 'shortdot' },
      title: { text: unit ? `Total [${unit}]` : undefined },
    },
    plotOptions: {
      column: {
        grouping: true,
        borderColor: 'transparent',
      },
    },
    tooltip: {
      headerFormat: '',
      pointFormatter: function () {
        const suffix = unit ? ` ${unit}` : '';
        // this.x is timestamp (ms) when series uses [x,y] points
        const dateLabel = this.x
          ? DateTime.fromMillis(this.x).toLocaleString(DateTime.DATE_SHORT)
          : this.category;
        return `<span>${dateLabel}</span><br/><span>${this.series.name}</span>: <b>${formatNumber(this.y, 'da-DK')}${suffix}</b>`;
      },
    },
    series: (data.series || []).map((s) => ({
      ...s,
      data: s.data || [],
    })),
  };

  return (
    <div style={{ width: '100%' }}>
      <HighchartsReact
        highcharts={Highcharts}
        containerProps={{ style: { height } }}
        options={options}
        ref={chartRef}
      />
    </div>
  );
};

export default ColumnChart;
